import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  HStack,
  Flex,
  Circle,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  ScrollText,
  Settings,
  UserCog,
  ClipboardCheck,
  ArrowRight,
  BarChart3,
  RefreshCw,
} from "lucide-react";

const TravelPolicySection = () => {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const accentColor = useColorModeValue("blue.500", "blue.300");
  const textColor = useColorModeValue("gray.700", "gray.200");

  const processSteps = [
    {
      icon: UserCog,
      title: "Evaluation & Analysis",
      description:
        "The account manager evaluates existing processes and identifies improvement opportunities.",
    },
    {
      icon: Settings,
      title: "Solution Implementation",
      description:
        "We implement innovative tools and policies tailored to specific needs.",
    },
    {
      icon: BarChart3,
      title: "Results Monitoring",
      description: "We track and measure the impact of implemented changes.",
    },
    {
      icon: RefreshCw,
      title: "Continuous Optimization",
      description: "We constantly refine the strategy for optimal results.",
    },
  ];

  return (
    <Box py={{ base: 12, md: 20 }} bg={bgColor}>
      <Container maxW="container.xl">
        <Grid
          templateColumns={{ base: "1fr", lg: "5fr 4fr" }}
          gap={{ base: 10, lg: 16 }}
        >
          {/* Left Content Section */}
          <GridItem>
            <VStack align="start" spacing={8}>
              <HStack spacing={4}>
                <Circle size="48px" bg="blue.500" color="white">
                  <ScrollText size={24} />
                </Circle>
                <Heading
                  as="h2"
                  size="xl"
                  color="blue.600"
                  fontFamily="Montserrat"
                >
                  Tailored travel policy
                </Heading>
              </HStack>

              <Text
                fontSize="lg"
                color={textColor}
                lineHeight="tall"
                fontFamily="Montserrat"
              >
                We work closely with clients to introduce innovative tools,
                processes, and policies, guiding travelers toward better booking
                decisions. A dedicated Key Account Manager evaluates the impact
                of these solutions, identifying further opportunities for
                performance improvement or process efficency.
              </Text>

              <Text
                fontSize="lg"
                color={textColor}
                lineHeight="tall"
                fontFamily="Montserrat"
              >
                We also support the implementation of the agreed solution
                sharing our best practices, following the results and refining
                along the way.
              </Text>

              {/* Features Grid */}
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={6}
                w="full"
                mt={4}
              >
                {[
                  { icon: ClipboardCheck, text: "Customized Policies" },
                  { icon: UserCog, text: "Dedicated Account Manager" },
                  { icon: Settings, text: "Optimized Processes" },
                  { icon: BarChart3, text: "Performance Monitoring" },
                ].map((feature, idx) => (
                  <Box
                    key={idx}
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="blue.200"
                    bg="blue.50"
                  >
                    <HStack spacing={3}>
                      <feature.icon size={20} color="#3182CE" />
                      <Text
                        color="blue.700"
                        fontWeight="medium"
                        fontFamily="Montserrat"
                      >
                        {feature.text}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </Grid>
            </VStack>
          </GridItem>

          {/* Right Process Section */}
          <GridItem>
            <VStack
              spacing={0}
              position="relative"
              align="stretch"
              bg={cardBg}
              borderRadius="xl"
              boxShadow="xl"
              p={8}
            >
              {processSteps.map((step, idx) => (
                <React.Fragment key={idx}>
                  <Flex gap={4} position="relative" zIndex={1}>
                    <VStack spacing={0} align="center">
                      <Circle
                        size="40px"
                        bg="blue.500"
                        color="white"
                        fontSize="lg"
                        fontWeight="bold"
                      >
                        {idx + 1}
                      </Circle>
                      {idx < processSteps.length - 1 && (
                        <Box w="2px" h="100px" bg="blue.200" my={2} />
                      )}
                    </VStack>
                    <Box pt={2} pb={8}>
                      <HStack spacing={2} mb={2}>
                        <step.icon size={20} color="#3182CE" />
                        <Text
                          fontSize="lg"
                          fontWeight="bold"
                          color="blue.700"
                          fontFamily="Montserrat"
                        >
                          {step.title}
                        </Text>
                      </HStack>
                      <Text color={textColor} fontFamily="Montserrat">
                        {step.description}
                      </Text>
                    </Box>
                  </Flex>
                </React.Fragment>
              ))}
            </VStack>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default TravelPolicySection;
